import type { Building } from "building-types/building/buildingTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { WindowShutters } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { getWindowHomeSettings } from "doc/elements/window/windowHomeSettings";
import { getMergeSettingsForOpeningParamsFromBuilding } from "doc/mergeSettingsSet";
import { makeWindowShutters } from "../elements/shutters";
import {
  getWindowShuttersPlacements,
  isWainscotingEnabledOnWindow,
} from "../generationTools/generateWindowShuttersTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  settings,
  settingsSets,
  planarGraphs,
  planarGraphPerimeters,
  windowPlacements: windows,
  windowShutters,
  roomOutlineTable,
  wallMasses,
  wainscoting,
  walls,
  optionChoices,
  blocks,
  mirror,
  doc,
}: Pick<
  Building,
  | "settings"
  | "settingsSets"
  | "planarGraphs"
  | "planarGraphPerimeters"
  | "windowPlacements"
  | "windowShutters"
  | "roomOutlineTable"
  | "wallMasses"
  | "wainscoting"
  | "walls"
  | "optionChoices"
  | "blocks"
  | "mirror"
  | "doc"
>) {
  return {
    windows,
    windowShuttersFromBuilding: windowShutters,
    planarGraphs,
    planarGraphPerimeters,
    roomOutlineTable,
    wallMasses,
    wainscotingSettings: settings.wainscoting,
    wainscoting,
    walls,
    blocks,
    mergeSettingsForOpeningParams: getMergeSettingsForOpeningParamsFromBuilding(
      { settingsSets, optionChoices, settings },
      doc,
    ),
    windowHomeSettings: getWindowHomeSettings(settings),
    mirror,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  mergeSettingsForOpeningParams: "deep",
  windowHomeSettings: "shallow",
});

function generate({
  windows,
  windowShuttersFromBuilding,
  planarGraphs,
  planarGraphPerimeters,
  wainscotingSettings,
  roomOutlineTable,
  wallMasses,
  wainscoting,
  walls,
  blocks,
  windowHomeSettings,
  mergeSettingsForOpeningParams,
  mirror,
}: ReturnType<typeof select>): { windowShutters: Table<WindowShutters>; issues: BuildingGenerationIssue[] } {
  const issues: BuildingGenerationIssue[] = [];
  const windowShutters = { ...windowShuttersFromBuilding };

  const windowShuttersPlacements = getWindowShuttersPlacements({
    blocks,
    planarGraphs,
    planarGraphPerimeters,
    windows,
    mergeSettingsForOpeningParams,
    windowHomeSettings,
    mirror,
  });

  for (const windowId in windowShuttersPlacements) {
    const shuttersPlacement = windowShuttersPlacements[windowId];

    const window = windows[windowId];

    const minZ = isWainscotingEnabledOnWindow(window, walls, wainscoting)
      ? wainscotingSettings.height
      : Number.NEGATIVE_INFINITY;

    const roomOutlines = window.frontRoom ? roomOutlineTable[window.frontRoom.id] : undefined;
    const wallMassSystem = window.frontRoom ? wallMasses[window.frontRoom.level.id] : undefined;

    const newShutters = makeWindowShutters({
      handle: window.handle,
      window,
      shuttersPlacement,
      roomOutlines,
      wallMassSystem,
      issues,
      minZ,
    });

    if (newShutters) {
      windowShutters[windowId] = newShutters;
    }
  }

  return { windowShutters, issues };
}

export default memo(select, generate, compare);
